/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderFormSchemaFactory, FormRootSchema, formulaControlValidator } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSchema,
  isRouteForCreationMode,
  NumberTypes,
  State,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode, getControl, getControlValue, UploadContext, UploadSlot } from '@gh/core-ui';
import { ComponentDetails } from '../../resources/component-and-item-details.model';
import { ComponentDetailsCommandService } from '../component-details-items-tab/component-details-command.service';
import { ComponentDetailsItemsState } from '../component-details-items-tab/component-details-items.state';
import { ComponentOrItemFormState } from '../component-or-item-form-state';

@Injectable()
export class ComponentDetailsFormState extends ComponentOrItemFormState<ComponentDetails> {
  pictureSlot: UploadSlot;
  locked: boolean = false;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private uploadContext: UploadContext,
              private itemsState: State<ComponentDetailsItemsState>,
              private commands: ComponentDetailsCommandService,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);

    this.pictureSlot = uploadContext.createUploadSlot({
      entryPoint: 'COMPONENT',
    });
  }

  get id(): ID {
    return getControlValue(this.group, 'id');
  }

  get unitOfMeasureId(): ID {
    return getControlValue(this.group, 'unitsOfMeasureId');
  }

  get cost(): number {
    return getControlValue(this.group, 'cost');
  }

  get dirty(): boolean {
    return this.group.dirty || this.itemsState.value.isDirty;
  }

  get submitValue(): ComponentDetails {
    return {
      ...this.group.getRawValue(),
      items: this.itemsState.value.items,
    };
  }

  build(component: ComponentDetails, merge?: boolean): void {
    this.locked = component.editLocked;

    this.initSchema(component);

    this.pictureSlot.setControl(getControl(this.group, 'picture'));

    if (merge) {
      this.commands.merge(component);
    } else {
      this.commands.load(component);
    }
  }

  isFormulaInvalid(): boolean {
    return getControl(this.group, 'formula').invalid;
  }

  markAsPristine(): void {
    super.markAsPristine();

    this.commands.markAsPristine();
  }

  protected buildSchema(_: ComponentDetails, { root }: FormStateSchema): FormRootSchema {
    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode : (this.userContext.hasPermission(
      BuilderPermission.Setup.Estimating.Components.EDIT) ? FormMode.EditMode : FormMode.ViewMode);

    const { locked } = this;

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        code: { required: true, enabled: !locked },
        name: { required: true, enabled: !locked },
        cost: { type: NumberTypes.Price, enabled: false },
        unitsOfMeasureId: { enabled: !locked },
        version: void 0,
        formula: { enabled: !locked, validators: [formulaControlValidator] },
        picture: { asyncValidators: [this.pictureSlot.formValidator()] },
        memo: { enabled: !locked },
        active: void 0,
        isFormulaNeedPropagation: void 0,
        oldFormula: void 0,
      },
    });
  }
}
